import { useState, useEffect, useCallback } from 'react'
import * as pdfjs from 'pdfjs-dist'

pdfjs.GlobalWorkerOptions.workerSrc = `${window.location.origin}/pdf.worker.min.mjs`

const RENDER_SCALE = 3

export interface UsePdfCanvasOptions {
	/** PDF source: URL string or File */
	source: string | File | null
	enabled?: boolean
}

export interface UsePdfCanvasReturn {
	numPages: number
	fullPages: HTMLCanvasElement[]
	isRendering: boolean
	error: string | null
}

export function usePdfCanvas({
	source,
	enabled = true,
}: UsePdfCanvasOptions): UsePdfCanvasReturn {
	const [numPages, setNumPages] = useState(0)
	const [fullPages, setFullPages] = useState<HTMLCanvasElement[]>([])
	const [isRendering, setIsRendering] = useState(false)
	const [error, setError] = useState<string | null>(null)

	const loadAndRender = useCallback(async (src: string | File) => {
		setError(null)
		setIsRendering(true)
		setFullPages([])
		setNumPages(0)

		try {
			const pdf =
				typeof src === 'string'
					? await pdfjs.getDocument(src).promise
					: await pdfjs.getDocument(await src.arrayBuffer()).promise

			const n = pdf.numPages
			setNumPages(n)

			const canvases: HTMLCanvasElement[] = []
			for (let pageNumber = 1; pageNumber <= n; pageNumber++) {
				const page = await pdf.getPage(pageNumber)
				const viewport = page.getViewport({ scale: RENDER_SCALE })
				const canvas = document.createElement('canvas')
				const ctx = canvas.getContext('2d')
				if (!ctx) continue
				canvas.width = viewport.width
				canvas.height = viewport.height
				await page.render({
					canvasContext: ctx,
					viewport,
				}).promise
				canvases.push(canvas)
			}

			setFullPages(canvases)
		} catch (err) {
			setError(err instanceof Error ? err.message : 'Failed to load PDF')
			setNumPages(0)
			setFullPages([])
		} finally {
			setIsRendering(false)
		}
	}, [])

	useEffect(() => {
		if (!enabled || source === null) return
		if (typeof source === 'string' && !source.trim()) return
		loadAndRender(source)
	}, [source, enabled, loadAndRender])

	return {
		numPages,
		fullPages,
		isRendering,
		error,
	}
}
